import React, { useState, useEffect, useMemo } from "react";
import {
  useReferalHistoryQuery,
  useUserListMutation,
} from "../redux/features/user/userApi";
import { DataGrid } from "@mui/x-data-grid";
import { FaUsers, FaChartLine } from "react-icons/fa";
import { FiSearch, FiDownload } from "react-icons/fi";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";
import Navbar from "../Components/Navbar";

const Referal = () => {
  const [selectedType, setSelectedType] = useState("solar_nft");

  const [referralData, setReferralData] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [snfttotalUsers, nftsetTotalUsers] = useState(0);
  const [sttotalUsers, nsetTotalUsers] = useState(0);

  // Get user list for total count
  const [userList, { data: userData }] = useUserListMutation();

  // Get referral history based on selected type
  const {
    data: referralHistoryData,
    isLoading,
    error,
  } = useReferalHistoryQuery("solar_nft");
  const { data: referralHistoryDatas } = useReferalHistoryQuery("kait_stake");

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // Fetch user list for total count
    userList();
  }, []);

  // useEffect(() => {
  //   // Update total users count
  //   if (userData && userData.data) {
  //     setTotalUsers(userData.data.length);
  //   }
  // }, [userData]);

  const formatReferralData = (items) => {
    if (!items || !Array.isArray(items)) return [];
    return items.map((item, index) => ({
      id: index + 1,
      ...item,
      date: item?.date ? new Date(item.date).toLocaleDateString() : "N/A",
      referral_type: item?.referral_type || "N/A",
      referred_user_id: item?.referreduserid || item?.referral_code || "N/A",
      referred_by_info: item?.referredby || "N/A",
    }));
  };

  useEffect(() => {
    // Update referral data when API response changes
    if (referralHistoryData && referralHistoryData.data) {
      const solarCount = referralHistoryData?.data?.length || 0;
      const kaitCount = referralHistoryDatas?.data?.length || 0;
      setTotalUsers(solarCount + kaitCount);
      nftsetTotalUsers(kaitCount);
      nsetTotalUsers(solarCount);
      const formattedData = formatReferralData(referralHistoryData.data);
      setReferralData(formattedData);
    }
  }, [referralHistoryData, referralHistoryDatas]);

  // Define table columns based on actual API response
  const columns = [
    { field: "id", headerName: "S.No", width: 80 },
    { field: "username", headerName: "Username", flex: 1, minWidth: 140 },
    { field: "user", headerName: "User Email", flex: 1.2, minWidth: 180 },
    { field: "referred_by_info", headerName: "Referred By", flex: 1, minWidth: 160 },
    { field: "referred_user_id", headerName: "Referred User ID", flex: 0.8, minWidth: 140 },
    {
      field: "referral_type",
      headerName: "Referral Type",
      width: 140,
      renderCell: (params) => {
        const typeClass =
          params.value === "solar_nft" ? "text-blue-500" : "text-green-500";
        return <span className={typeClass}>{params.value}</span>;
      },
    },
    { field: "date", headerName: "Date", width: 140 },
  ];

  const handleTypeClick = (type) => {
    setSelectedType(type);
    if (type === "kait_stake") {
      const formattedData = formatReferralData(referralHistoryDatas?.data || []);
      setReferralData(formattedData);
    } else {
      const formattedData = formatReferralData(referralHistoryData?.data || []);
      setReferralData(formattedData);
    }
  };

  const displayedRows = useMemo(() => {
    if (!searchQuery) return referralData;
    const q = searchQuery.toLowerCase();
    return referralData.filter((row) => {
      const username = String(row.username || "").toLowerCase();
      const userEmail = String(row.user || "").toLowerCase();
      const referredBy = String(row.referred_by_info || "").toLowerCase();
      const referredId = String(row.referred_user_id || "").toLowerCase();
      return (
        username.includes(q) ||
        userEmail.includes(q) ||
        referredBy.includes(q) ||
        referredId.includes(q)
      );
    });
  }, [referralData, searchQuery]);

  const handleExportCsv = () => {
    const headers = [
      "S.No",
      "Username",
      "User Email",
      "Referred By",
      "Referred User ID",
      "Referral Type",
      "Date",
    ];
    const rows = displayedRows.map((r) => [
      r.id,
      r.username || "",
      r.user || "",
      r.referred_by_info || "",
      r.referred_user_id || "",
      r.referral_type || "",
      r.date || "",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `referrals_${selectedType}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center h-screen">
          <Lottie animationData={loader} loop={true} />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="flex">
        <div className="w-[80vw] mx-auto p-6">
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Referral Management
            </h1>
            <p className="text-gray-600">
              Manage and view referral history and statistics
            </p>
          </div>

          {/* Combined Statistics Section */}
          <div className="mb-6">
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-800">
                  Dashboard Overview
                </h2>
                <div className="flex items-center space-x-2 bg-gray-100 rounded-md p-1">
                  <button
                    onClick={() => handleTypeClick("solar_nft")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedType === "solar_nft"
                        ? "bg-white text-blue-600 shadow"
                        : "text-gray-700 hover:text-blue-600"
                    }`}
                  >
                    Solar NFT
                  </button>
                  <button
                    onClick={() => handleTypeClick("kait_stake")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedType === "kait_stake"
                        ? "bg-white text-blue-600 shadow"
                        : "text-gray-700 hover:text-blue-600"
                    }`}
                  >
                    Kait Stake
                  </button>
                </div>
              </div>

              {/* Combined Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Total Users Card */}
                {/* <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 rounded-lg text-white">
                   <div className="flex items-center justify-between">
                     <div>
                       <p className="text-sm text-blue-100 font-medium">Total Users</p>
                       <p className="text-3xl font-bold">{totalUsers}</p>
                       <p className="text-xs text-blue-200">Registered users</p>
                     </div>
                     <div className="bg-blue-400 p-3 rounded-full">
                       <FaUsers className="text-white text-2xl" />
                     </div>
                   </div>
                 </div> */}

                {/* Total Referrals Card */}
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 rounded-lg text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-100 font-medium">
                        Total Referrals
                      </p>
                      <p className="text-3xl font-bold">{totalUsers}</p>
                      <p className="text-xs text-purple-200">All types</p>
                    </div>
                    <div className="bg-purple-400 p-3 rounded-full">
                      <FaChartLine className="text-white text-2xl" />
                    </div>
                  </div>
                </div>

                {/* Solar NFT Card */}
                <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 rounded-lg text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-100 font-medium">
                        Solar NFT
                      </p>
                      <p className="text-3xl font-bold">
                        {sttotalUsers}
                        {/* {
                          referralData.filter(
                            (item) => item.referral_type === "solar_nft"
                          ).length
                        } */}
                      </p>
                      <p className="text-xs text-green-200">Referrals</p>
                    </div>
                    <div className="bg-green-400 p-3 rounded-full">
                      <FaChartLine className="text-white text-2xl" />
                    </div>
                  </div>
                </div>

                {/* Kait Stake Card */}
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 rounded-lg text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-orange-100 font-medium">
                        Kait Stake
                      </p>
                      <p className="text-3xl font-bold">
                        {/* {
                          referralData.filter(
                            (item) => item.referral_type === "kait_stake"
                          ).length
                        } */}
                        {snfttotalUsers}
                      </p>
                      <p className="text-xs text-orange-200">Referrals</p>
                    </div>
                    <div className="bg-orange-400 p-3 rounded-full">
                      <FaChartLine className="text-white text-2xl" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <h2 className="text-xl font-semibold text-gray-800">
                Referral History
              </h2>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search user, email, referred by..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-3 py-2 w-72 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    onClick={handleExportCsv}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <FiDownload />
                    Export CSV
                  </button>
                </div>
              </div>
            </div>

            {/* Data Table */}
            <div className="p-6">
              {error ? (
                <div className="text-center py-8">
                  <p className="text-red-600 text-lg">
                    Error loading referral data
                  </p>
                  <p className="text-gray-500 mt-2">Please try again later</p>
                </div>
              ) : (
                <div className="w-full overflow-x-hidden">
                  <DataGrid
                    rows={displayedRows}
                    columns={columns}
                    autoHeight
                    density="compact"
                    disableColumnMenu
                    pageSize={10}
                    rowsPerPageOptions={[10, 20, 50]}
                    disableSelectionOnClick
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#f9fafb",
                        borderBottom: "2px solid #e5e7eb",
                        color: "#374151",
                        fontWeight: 600,
                      },
                      "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #e5e7eb",
                      },
                      "& .MuiDataGrid-row:nth-of-type(odd)": {
                        backgroundColor: "#fcfcfd",
                      },
                      "& .MuiDataGrid-row:hover": {
                        backgroundColor: "#fafafa",
                      },
                      "& .MuiDataGrid-virtualScroller": {
                        overflowX: "hidden !important",
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Referal;
